import { useMemo, useState, useCallback } from 'react';
import SmartImg from './SmartImg.jsx';
import GalleryLightbox from './GalleryLightbox.jsx';
import { GALLERY_ITEMS } from '../data/siteImages.js';
import { buildGallerySlides, countSlidesInGalleryItem } from '../utils/gallerySlides.js';

export default function GallerySection({ titleTag: Title = 'h2' }) {
  const slides = useMemo(() => buildGallerySlides(GALLERY_ITEMS), []);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openAt = useCallback((i) => {
    if (i >= 0 && i < slides.length) setLightboxIndex(i);
  }, [slides.length]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const navigateTo = useCallback(
    (i) => {
      if (slides.length === 0) return;
      const wrapped = ((i % slides.length) + slides.length) % slides.length;
      setLightboxIndex(wrapped);
    },
    [slides.length]
  );

  const blocks = useMemo(() => {
    let base = 0;
    return GALLERY_ITEMS.map((item, i) => {
      const slideBase = base;
      base += countSlidesInGalleryItem(item);
      return { item, i, slideBase };
    });
  }, []);

  return (
    <section id="gallery" className="section gallery">
      <div className="container">
        <Title>Our work</Title>
        <p className="section-intro gallery-hint">Click any photo to enlarge. Use arrow buttons or keyboard arrows to browse all images.</p>
        <div className="gallery-grid">
          {blocks.map(({ item, i, slideBase }) => {
            if (item.type === 'beforeAfter') {
              return (
                <div className="gallery-item gallery-item--wide before-after-row" key={`gallery-ba-${i}`}>
                  {item.title ? <h3 className="before-after-heading">{item.title}</h3> : null}
                  <div className="before-after-pair">
                    <figure className="before-after-figure">
                      <span className="before-after-label">Before</span>
                      <button
                        type="button"
                        className="gallery-thumb"
                        onClick={() => openAt(slideBase)}
                        aria-label={`Open larger: ${item.beforeAlt}`}
                      >
                        <div className="gallery-frame">
                          <SmartImg
                            src={item.before}
                            alt={item.beforeAlt}
                            width={800}
                            height={600}
                            loading="lazy"
                          />
                        </div>
                      </button>
                    </figure>
                    <figure className="before-after-figure">
                      <span className="before-after-label">After</span>
                      <button
                        type="button"
                        className="gallery-thumb"
                        onClick={() => openAt(slideBase + 1)}
                        aria-label={`Open larger: ${item.afterAlt}`}
                      >
                        <div className="gallery-frame">
                          <SmartImg
                            src={item.after}
                            alt={item.afterAlt}
                            width={800}
                            height={600}
                            loading="lazy"
                          />
                        </div>
                      </button>
                    </figure>
                  </div>
                </div>
              );
            }

            if (item.type === 'workPair' && Array.isArray(item.photos)) {
              return (
                <div className="gallery-item gallery-item--wide work-pair-row" key={`gallery-wp-${i}`}>
                  {item.title ? <h3 className="before-after-heading">{item.title}</h3> : null}
                  <div className="before-after-pair">
                    {item.photos.map((photo, j) => (
                      <figure className="before-after-figure work-pair-figure" key={`gallery-wp-${i}-${j}`}>
                        <button
                          type="button"
                          className="gallery-thumb"
                          onClick={() => openAt(slideBase + j)}
                          aria-label={`Open larger: ${photo.alt}`}
                        >
                          <div className="gallery-frame">
                            <SmartImg
                              src={photo.src}
                              alt={photo.alt}
                              width={800}
                              height={600}
                              loading="lazy"
                            />
                          </div>
                        </button>
                      </figure>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <figure
                className={`gallery-item${item.wide ? ' gallery-item--wide' : ''}`}
                key={`gallery-${i}`}
              >
                <button
                  type="button"
                  className="gallery-thumb"
                  onClick={() => openAt(slideBase)}
                  aria-label={`Open larger: ${item.alt}`}
                >
                  <div className="gallery-frame">
                    <SmartImg
                      src={item.src}
                      alt={item.alt}
                      width={item.wide ? 1200 : 800}
                      height={item.wide ? 500 : 600}
                      loading="lazy"
                    />
                  </div>
                </button>
              </figure>
            );
          })}
        </div>
      </div>
      {lightboxIndex !== null && slides.length > 0 ? (
        <GalleryLightbox
          slides={slides}
          index={lightboxIndex}
          onClose={closeLightbox}
          onNavigate={navigateTo}
        />
      ) : null}
    </section>
  );
}
